// SEQ-180 fiscal year and date preset helpers for the P&L analytics page.
//
// WHY THIS IS DATE-ONLY ARITHMETIC. The claim timing defect this repo already
// carries a fix for (src/lib/claimTiming.ts) came from building a local Date
// and then formatting it with `toISOString()`, which reads UTC components and
// silently loses a day east of UTC. This module avoids that failure mode a
// different way: every Date it builds or receives from date-fns is read back
// with date-fns's own formatter (`format`), which uses local getters, never
// `toISOString()`. As long as construction and formatting both stay on the
// local clock, the two cancel out and the result is identical in every
// timezone, which fiscalYear.test.ts proves by running under both
// TZ=Australia/Brisbane and TZ=UTC (npm run verify:fiscal-year).
//
// Australian financial year: 1 July to 30 June, named by the ending year
// (1 July 2025 to 30 June 2026 is "FY26").

import {
  addMonths,
  differenceInCalendarMonths,
  endOfMonth,
  format,
  isValid,
  parse,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subMonths,
  subQuarters,
  subYears,
} from "date-fns";

const ISO_DATE_FORMAT = "yyyy-MM-dd";
const ISO_REFERENCE = new Date(1970, 0, 1);

/** An inclusive date range, `from`/`to` as ISO yyyy-MM-dd, or both null for "no bound" (project lifetime). */
export interface DateRange {
  from: string | null;
  to: string | null;
}

function parseIso(iso: string): Date {
  const parsed = parse(iso, ISO_DATE_FORMAT, ISO_REFERENCE);
  if (!isValid(parsed)) {
    throw new Error(`Invalid ISO date: ${iso}`);
  }
  return parsed;
}

function formatIso(date: Date): string {
  return format(date, ISO_DATE_FORMAT);
}

/**
 * The Australian financial year a date falls in, named by its ending year.
 * 30 June is the last day of the year it names; 1 July is the first day of
 * the next one.
 */
export function fiscalYearOf(dateIso: string): number {
  const date = parseIso(dateIso);
  const month = date.getMonth() + 1; // 1-12, local
  const year = date.getFullYear();
  return month >= 7 ? year + 1 : year;
}

/** The 1 July to 30 June range for a financial year named by its ending year. */
export function fiscalYearRange(fy: number): DateRange {
  const startYear = fy - 1;
  return {
    from: `${String(startYear).padStart(4, "0")}-07-01`,
    to: `${String(fy).padStart(4, "0")}-06-30`,
  };
}

/**
 * "FY26" for financial year 2026 (1 July 2025 to 30 June 2026). Accepts
 * either the financial year number directly or an ISO date to resolve first.
 */
export function fiscalYearLabel(input: number | string): string {
  const fy = typeof input === "number" ? input : fiscalYearOf(input);
  return `FY${String(fy % 100).padStart(2, "0")}`;
}

/** The named date-range presets the P&L analytics filter bar offers. */
export type DatePresetName =
  | "this_month"
  | "last_month"
  | "quarter_to_date"
  | "year_to_date"
  | "financial_year_to_date"
  | "last_financial_year"
  | "project_lifetime"
  | "custom";

/**
 * Resolve a named preset to a concrete range as of `todayIso`. "*_to_date"
 * presets are month/quarter/year/FY-to-date, so `to` is today, not the end of
 * the period. `project_lifetime` returns `{ from: null, to: null }`, meaning
 * "no date filter". `custom` requires the caller's own range.
 */
export function datePreset(
  name: DatePresetName,
  todayIso: string,
  custom?: DateRange,
): DateRange {
  const today = parseIso(todayIso);

  switch (name) {
    case "this_month":
      return { from: formatIso(startOfMonth(today)), to: todayIso };
    case "last_month": {
      const prev = subMonths(today, 1);
      return { from: formatIso(startOfMonth(prev)), to: formatIso(endOfMonth(prev)) };
    }
    case "quarter_to_date":
      return { from: formatIso(startOfQuarter(today)), to: todayIso };
    case "year_to_date":
      return { from: formatIso(startOfYear(today)), to: todayIso };
    case "financial_year_to_date": {
      const { from } = fiscalYearRange(fiscalYearOf(todayIso));
      return { from, to: todayIso };
    }
    case "last_financial_year":
      return fiscalYearRange(fiscalYearOf(todayIso) - 1);
    case "project_lifetime":
      return { from: null, to: null };
    case "custom":
      if (!custom) {
        throw new Error('datePreset("custom", ...) requires a custom range');
      }
      return custom;
    default: {
      const exhaustive: never = name;
      throw new Error(`Unknown date preset: ${String(exhaustive)}`);
    }
  }
}

/** Which prior period a comparison is against. */
export type ComparisonKind =
  | "previous_month"
  | "previous_quarter"
  | "previous_year"
  | "previous_financial_year";

/**
 * The prior period to compare a range against, in whole calendar months.
 *
 * Every P&L row is dated the 1st of its month (`period_month`), so the data
 * can only be cut on month boundaries. The range is snapped to the whole
 * months it touches, shifted back by one unit of `kind`, and the prior range
 * covers the same number of whole months. "Last month" (October) therefore
 * compares with September only. The earlier version held the day count fixed,
 * which for October ended on 1 October and pulled the whole of October into
 * the prior period (SEQ-240 audit B, row 21).
 *
 * A to-date range keeps its last month whole: year to date on 3 Nov 2026
 * compares with January to November 2025. `project_lifetime` (both bounds
 * null) has no comparable prior period and returns `{ from: null, to: null }`.
 */
export function comparisonPeriod(range: DateRange, kind: ComparisonKind): DateRange {
  if (range.from === null || range.to === null) {
    return { from: null, to: null };
  }

  const from = startOfMonth(parseIso(range.from));
  const to = parseIso(range.to);
  const monthCount = differenceInCalendarMonths(to, from) + 1;

  let shiftedFrom: Date;
  switch (kind) {
    case "previous_month":
      shiftedFrom = subMonths(from, 1);
      break;
    case "previous_quarter":
      shiftedFrom = subQuarters(from, 1);
      break;
    case "previous_year":
    case "previous_financial_year":
      shiftedFrom = subYears(from, 1);
      break;
    default: {
      const exhaustive: never = kind;
      throw new Error(`Unknown comparison kind: ${String(exhaustive)}`);
    }
  }

  const shiftedTo = endOfMonth(addMonths(shiftedFrom, monthCount - 1));
  return { from: formatIso(shiftedFrom), to: formatIso(shiftedTo) };
}
